import { useEffect, useCallback, useRef } from 'react';
import { eventBus, StockEvents } from '@/lib/eventBus';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/lib/toast';
import { enqueueEffect, getSyncQueue, removeEffect, recordEffectFailure } from '@/lib/syncQueue';
import { formatLocalDate } from '@/lib/dateUtils';

function getPersistedLegacyCategoryId(categoryId?: string, tagIds: string[] = []) {
  if (!categoryId) return null;
  return tagIds.includes(categoryId) ? null : categoryId;
}

export function StockEventHandler(): null {
  const isProcessingRef = useRef(false);

  const processQueue = useCallback(async () => {
    if (isProcessingRef.current || !navigator.onLine) return;

    const queue = getSyncQueue();
    const stockEvents = queue.filter(e => 
      e.type.startsWith('STOCK_') || 
      (e.payload && (e.payload.type === 'package-create' || e.payload.type === 'package-update' || e.payload.type === 'package-delete'))
    );

    if (stockEvents.length === 0) return;

    isProcessingRef.current = true;
    console.log(`StockEventHandler: Processing ${stockEvents.length} pending actions`);

    for (const effect of stockEvents) {
      let lastError: unknown = null;
      try {
        let success = false;
        
        // Drop effects older than 7 days — they are almost certainly obsolete.
        // (Previous threshold of 5 minutes dropped legitimate offline-weekend mutations.)
        const ageMs = Date.now() - new Date(effect.timestamp).getTime();
        if (ageMs > 7 * 24 * 60 * 60 * 1000) {
          console.warn('StockEventHandler: Removing stale (>7d) effect', effect.id, effect.type);
          removeEffect(effect.id);
          continue;
        }
        
        if (effect.type === StockEvents.CREATE) {
          const { item, teamId } = effect.payload;
          const { error } = await supabase.from('stock_items').insert({
            team_id: teamId,
            name: item.name,
            description: item.description || null,
            unit: item.unit,
            image_url: item.imageUrl || null,
            current_quantity: item.currentQuantity,
            package_size: item.packageSize,
            storage_location_id: item.storageLocationId || null,
            active_location_id: item.activeLocationId || null,
            active_quantity: item.activeQuantity ?? 0,
            reorder_threshold: item.reorderThreshold,
            refill_threshold: item.refillThreshold,
            category: item.category,
            category_id: getPersistedLegacyCategoryId(item.categoryId, item.tagIds || []),
            tag_ids: item.tagIds || [],
            bundle_unit: item.bundleUnit || 'doos',
            webshop_links: item.webshopLinks || [],
            expiry_date: item.expiryDate ? formatLocalDate(new Date(item.expiryDate)) : null,
            expiry_warning_days: item.expiryWarningDays ?? 14,
          });
          if (error) console.error('StockEventHandler CREATE failed:', error);
          success = !error;
          if (error) lastError = error;
        }
        else if (effect.type === StockEvents.UPDATE) {
          if (effect.payload.type === 'package-create') {
            const p = effect.payload.payload || effect.payload;
            const { stockItemId, quantity, count = 1, status, purchaseDate, expiryDate } = p;
            const teamId = p.teamId || effect.payload.teamId;
            if (!teamId || !stockItemId) { removeEffect(effect.id); continue; }
            const packagesToInsert = Array.from({ length: count }, () => ({
                stock_item_id: stockItemId,
                team_id: teamId,
                quantity,
                original_quantity: quantity,
                status: status || 'storage',
                purchase_date: purchaseDate ? formatLocalDate(new Date(purchaseDate)) : null,
                expiry_date: expiryDate ? formatLocalDate(new Date(expiryDate)) : null,
            }));
            const { error } = await supabase.from('stock_packages').insert(packagesToInsert);
            success = !error;
            if (error) lastError = error;
          } else if (effect.payload.type === 'package-update') {
            const p = effect.payload.payload || effect.payload;
            const { id, updates } = p;
            if (!id) { removeEffect(effect.id); continue; }
            const dbUpdates: any = {};
            if (updates.quantity !== undefined) dbUpdates.quantity = updates.quantity;
            if (updates.status !== undefined) dbUpdates.status = updates.status;
            if (updates.locationId !== undefined) dbUpdates.location_id = updates.locationId;
            const { error } = await supabase.from('stock_packages').update(dbUpdates).eq('id', id);
            success = !error;
            if (error) lastError = error;
          } else {
            const { id, updates } = effect.payload;
            if (!id) { removeEffect(effect.id); continue; }
            const dbUpdates: any = {};
            if (updates.currentQuantity !== undefined) dbUpdates.current_quantity = updates.currentQuantity;
            if (updates.name !== undefined) dbUpdates.name = updates.name;
            if (updates.description !== undefined) dbUpdates.description = updates.description;
            if (updates.imageUrl !== undefined) dbUpdates.image_url = updates.imageUrl;
            if (updates.tagIds !== undefined) dbUpdates.tag_ids = updates.tagIds;
            if (updates.categoryId !== undefined) {
              dbUpdates.category_id = getPersistedLegacyCategoryId(updates.categoryId, updates.tagIds || []);
            }
            if (updates.favourite !== undefined) dbUpdates.favourite = updates.favourite;
            if (updates.parLevel !== undefined) dbUpdates.par_level = updates.parLevel;
            if (updates.lastCheckedAt !== undefined) {
              dbUpdates.last_checked_at = updates.lastCheckedAt instanceof Date
                ? updates.lastCheckedAt.toISOString()
                : updates.lastCheckedAt;
            }
            const { error } = await supabase.from('stock_items').update(dbUpdates).eq('id', id);
            success = !error;
            if (error) lastError = error;
          }
        }
        else if (effect.type === StockEvents.RECORD_USAGE || effect.type === StockEvents.ADD_STOCK) {
          const { itemId, teamId, quantity, memberId, deviceId, notes } = effect.payload;
          if (!itemId || !teamId) { removeEffect(effect.id); continue; }

          const { error: txError } = await supabase
            .from('stock_transactions')
            .insert({
              stock_item_id: itemId,
              team_id: teamId,
              quantity_change: effect.type === StockEvents.RECORD_USAGE ? -quantity : quantity,
              transaction_type: effect.type === StockEvents.RECORD_USAGE ? 'use' : 'purchase',
              notes,
              created_by_member_id: memberId,
              created_by_device_id: deviceId,
            });

          const { error: updateError } = await supabase
            .from('stock_items')
            .update({ current_quantity: effect.payload.newQuantity })
            .eq('id', itemId);

          success = !txError && !updateError;
          if (txError || updateError) lastError = txError || updateError;
        }
        else if (effect.type === StockEvents.DELETE) {
           if (effect.payload.type === 'package-delete') {
             const p = effect.payload.payload || effect.payload;
             const { id } = p;
             if (!id) { removeEffect(effect.id); continue; }
             const { error } = await supabase.from('stock_packages').delete().eq('id', id);
             success = !error;
             if (error) lastError = error;
           } else {
             const { id } = effect.payload;
             if (!id) { removeEffect(effect.id); continue; }
             const { error } = await supabase.from('stock_items').delete().eq('id', id);
             success = !error;
             if (error) lastError = error;
           }
        }

        if (success) {
          removeEffect(effect.id);
        } else {
          // Track failure with bounded retry; effect is parked in the dead-letter
          // queue after SYNC_MAX_ATTEMPTS so the user can review/retry/discard it
          // instead of losing data silently. See FailedSyncRecovery in Settings.
          const parked = recordEffectFailure(effect.id, lastError ?? 'Unknown stock sync error');
          if (parked) {
            console.warn('StockEventHandler: effect moved to dead-letter queue', effect.id, lastError);
          }
        }
      } catch (err) {
        console.error('Failed to process stock sync effect:', effect, err);
        recordEffectFailure(effect.id, err);
      }
    }

    isProcessingRef.current = false;
  }, []);

  useEffect(() => {
    const handleCreate = (payload: any) => {
      const isOffline = !navigator.onLine;
      if (isOffline) {
        enqueueEffect(StockEvents.CREATE, payload);
        toast.info(`Offline: ${payload.item.name} in wachtrij`, { channel: 'stock' });
      } else {
        // Immediate sync if online
        enqueueEffect(StockEvents.CREATE, payload);
        processQueue();
        toast.success(`${payload.item.name} toegevoegd`, { channel: 'stock' });
      }
    };

    const handleUpdate = (payload: any) => {
      enqueueEffect(StockEvents.UPDATE, payload);
      if (navigator.onLine) processQueue();
    };

    const handleDelete = (payload: any) => {
      enqueueEffect(StockEvents.DELETE, payload);
      if (navigator.onLine) processQueue();
      if (payload.name) toast.success(`${payload.name} verwijderd`, { channel: 'stock' });
    };

    const handleUsage = (payload: any) => {
      enqueueEffect(StockEvents.RECORD_USAGE, payload);
      if (navigator.onLine) {
        processQueue();
        toast.success(`${payload.itemName}: -${payload.quantity} ${payload.unit}`, { channel: 'stock' });
      } else {
        toast.info(`Offline: -${payload.quantity} ${payload.unit} in wachtrij`, { channel: 'stock' });
      }
    };

    const handleAddStock = (payload: any) => {
      enqueueEffect(StockEvents.ADD_STOCK, payload);
      if (navigator.onLine) {
        processQueue();
        toast.success(`${payload.itemName}: +${payload.quantity} ${payload.unit}`, { channel: 'stock' });
      } else {
        toast.info(`Offline: +${payload.quantity} ${payload.unit} in wachtrij`, { channel: 'stock' });
      }
    };

    eventBus.on(StockEvents.CREATE, handleCreate);
    eventBus.on(StockEvents.UPDATE, handleUpdate);
    eventBus.on(StockEvents.DELETE, handleDelete);
    eventBus.on(StockEvents.RECORD_USAGE, handleUsage);
    eventBus.on(StockEvents.ADD_STOCK, handleAddStock);

    window.addEventListener('online', processQueue);
    processQueue();

    return () => {
      eventBus.off(StockEvents.CREATE, handleCreate);
      eventBus.off(StockEvents.UPDATE, handleUpdate);
      eventBus.off(StockEvents.DELETE, handleDelete);
      eventBus.off(StockEvents.RECORD_USAGE, handleUsage);
      eventBus.off(StockEvents.ADD_STOCK, handleAddStock);
      window.removeEventListener('online', processQueue);
    };
  }, [processQueue]);

  return null;
}
